import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, User, LogOut, ChevronDown, ShieldCheck, Wallet, Truck } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import Ticker from '@/components/Ticker';
import axios from 'axios';
import { trackSearch, trackCartOpened } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component - Nova logo vermelha 3D
const Logo = ({ className = "", compact = false, isHomePage = true }) => (
  <div className={`flex items-center ${isHomePage ? '' : 'justify-center'} ${className}`}>
    <div className="relative flex items-center">
      <picture>
        <source srcSet="/logo-new.webp" type="image/webp" />
        <img 
          src="/logo-new-opt.png" 
          alt="Outlet Premium" 
          className={`${compact ? 'h-10' : 'h-12 md:h-14'} w-auto object-contain`}
          loading="eager"
        />
      </picture>
      {!compact && isHomePage && (
        <div className="flex flex-col leading-none ml-1.5 md:ml-2">
          <span 
            className="font-semibold text-[22px] md:text-[26px] tracking-tight text-white"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
          >
            OUTLET
          </span>
          <span 
            className="font-medium text-[9px] md:text-[11px] tracking-[0.35em] uppercase -mt-0.5 ml-1 md:ml-1.5 flex items-baseline gap-[1px]"
            style={{ fontFamily: "'Montserrat', 'DM Sans', sans-serif", color: '#787674' }}
          >
            PREMIUM
            <span style={{ fontSize: '6px', letterSpacing: '0.1em', color: '#5a5856' }}>LTDA</span>
          </span>
        </div>
      )}
    </div>
  </div>
);

const subcategories = {
  Masculino: ['Camisetas', 'Camisas', 'Calças', 'Bermudas', 'Country'],
  Feminino: ['Fitness', 'Vestidos', 'Blusas', 'Shorts', 'Country'],
  Infantil: ['Meninos', 'Meninas'],
};

export const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const searchRef = useRef(null);
  const { cartCount } = useCart();
  const { user, isAdmin, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [cashbackBalance, setCashbackBalance] = useState(0);

  // Fetch cashback balance — robust against transient network/timeout errors.
  // On failure we keep the last known balance (never reset to 0) and retry once,
  // so the badge doesn't silently disappear.
  const fetchCashbackBalance = useCallback(async (retry = true) => {
    try {
      const res = await axios.get(`${API}/cashback/balance`);
      setCashbackBalance(res.data.balance || 0);
    } catch (e) {
      if (retry) {
        setTimeout(() => fetchCashbackBalance(false), 1500);
      }
      // keep previous balance on failure (do NOT reset to 0)
    }
  }, []);

  // Fetch when authentication state or route changes
  useEffect(() => {
    if (!isAuthenticated) { setCashbackBalance(0); return; }
    fetchCashbackBalance();
  }, [isAuthenticated, location.pathname, fetchCashbackBalance]);

  // Refresh balance when the tab regains focus
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => fetchCashbackBalance(false);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isAuthenticated, fetchCashbackBalance]);
  
  // Header preto apenas na home, #f7f7f7 nas outras páginas
  const isHomePage = location.pathname === '/';
  const useDarkHeader = isHomePage;
  const headerBg = useDarkHeader ? 'bg-[#0d0d0d]' : 'bg-[#f7f7f7]';
  const textColor = useDarkHeader ? 'text-white' : 'text-gray-900';
  const textColorMuted = useDarkHeader ? 'text-white/80' : 'text-gray-700';
  const textColorPlaceholder = useDarkHeader ? 'placeholder:text-white/50' : 'placeholder:text-gray-400';
  const hoverBg = useDarkHeader ? 'hover:bg-white/10' : 'hover:bg-gray-100';
  const borderColor = useDarkHeader ? 'border-white/5' : 'border-gray-200';
  const searchBg = useDarkHeader ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-200 text-gray-900';
  const searchIconColor = useDarkHeader ? 'text-white/50' : 'text-gray-400';

  // Fetch products for autocomplete
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setAllProducts(res.data);
      } catch (e) {
        console.error('Error fetching products for search:', e);
      }
    };
    fetchProducts();
  }, []);

  // Handle search autocomplete
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const filtered = allProducts.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5);
      setSearchSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, allProducts]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackSearch(searchQuery.trim());
      navigate(`/produtos?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    navigate(`/produto/${product.product_id}`);
    setSearchQuery('');
    setShowSuggestions(false);
    setSearchOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className={`sticky top-0 z-40 ${headerBg} border-b ${borderColor}`}>
      {/* Top bar - Ticker com transição 1 em 1 */}
      <Ticker />

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20 relative">
          {/* Mobile menu + Cashback (agrupados pra ficar grudados na esquerda) */}
          <div className="md:hidden flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={`${textColor} ${hoverBg}`} data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] overflow-y-auto bg-[#0d0d0d]">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    {/* Logo com mesma cor da página inicial */}
                    <div className="flex items-center">
                      <picture>
                        <source srcSet="/logo-new.webp" type="image/webp" />
                        <img 
                          src="/logo-new-opt.png" 
                          alt="Outlet Premium" 
                          className="h-10 w-auto object-contain"
                        />
                      </picture>
                      <div className="flex flex-col ml-1.5">
                        <span 
                          className="text-white font-semibold text-[20px] tracking-tight"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          OUTLET
                        </span>
                        <span 
                          className="font-medium text-[8px] tracking-[0.35em] uppercase -mt-0.5 flex items-baseline gap-[1px]"
                          style={{ fontFamily: "'Montserrat', 'DM Sans', sans-serif", color: '#787674' }}
                        >
                          PREMIUM
                          <span style={{ fontSize: '5px', letterSpacing: '0.1em', color: '#5a5856' }}>LTDA</span>
                        </span>
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 space-y-2">
                  {/* User Account Section - Mobile */}
                  <div className="border-b border-white/10 pb-3 mb-3">
                    {isAuthenticated ? (
                      <>
                        <div className="px-4 py-2 mb-2">
                          <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                          <p className="text-white/50 text-xs truncate">{user?.email}</p>
                        </div>
                        {cashbackBalance > 0 && (
                          <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-emerald-900/40 border border-emerald-700/30" data-testid="cashback-balance-mobile">
                            <div className="flex items-center gap-2">
                              <Wallet className="h-4 w-4 text-emerald-400" />
                              <div>
                                <p className="text-[9px] font-medium text-emerald-400 uppercase tracking-wide">Cashback</p>
                                <p className="text-sm font-bold text-emerald-300">R$ {cashbackBalance.toFixed(2).replace('.', ',')}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <SheetClose asChild>
                          <Link
                            to="/minha-conta"
                            className="flex items-center gap-2 py-2 px-4 text-sm text-white/70 hover:text-white"
                            data-testid="mobile-nav-minha-conta"
                          >
                            <Wallet className="h-4 w-4" />
                            Minha Conta
                          </Link>
                        </SheetClose>
                        {isAdmin && (
                          <SheetClose asChild>
                            <Link
                              to="/admin"
                              className="flex items-center gap-2 py-2 px-4 text-sm text-white/70 hover:text-white"
                              data-testid="mobile-nav-admin"
                            >
                              <ShieldCheck className="h-4 w-4" />
                              Painel Admin
                            </Link>
                          </SheetClose>
                        )}
                        <SheetClose asChild>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 py-2 px-4 text-sm text-red-400 hover:text-red-300"
                            data-testid="mobile-logout-btn"
                          >
                            <LogOut className="h-4 w-4" />
                            Sair da conta
                          </button>
                        </SheetClose>
                      </>
                    ) : (
                      <SheetClose asChild>
                        <Link
                          to="/entrar"
                          className="flex items-center gap-2 py-2 px-4 text-base font-medium text-white hover:bg-white/10 rounded-lg"
                          data-testid="mobile-login-link"
                        >
                          <User className="h-5 w-5" />
                          Entrar / Criar conta
                        </Link>
                      </SheetClose>
                    )}
                  </div>

                  {/* Ver Todos os Produtos - antes das categorias */}
                  <div className="border-b border-white/10 pb-3 mb-3">
                    <SheetClose asChild>
                      <Link
                        to="/produtos"
                        className="block py-2 px-4 text-lg font-semibold text-white"
                        data-testid="mobile-nav-todos"
                      >
                        Ver Todos os Produtos
                      </Link>
                    </SheetClose>
                  </div>

                  {/* Categories with subcategories */}
                  {['Masculino', 'Feminino', 'Infantil'].map((cat) => (
                    <div key={cat} className="border-b border-white/10 pb-3 mb-3">
                      <SheetClose asChild>
                        <Link
                          to={`/produtos/${cat.toLowerCase()}`}
                          className="block py-2 px-4 text-lg font-semibold text-white"
                          data-testid={`mobile-nav-${cat.toLowerCase()}`}
                        >
                          {cat}
                        </Link>
                      </SheetClose>
                      <div className="pl-4 space-y-1">
                        {subcategories[cat]?.map((sub) => (
                          <SheetClose asChild key={sub}>
                            <Link
                              to={`/produtos/${cat.toLowerCase()}?subcategory=${sub.toLowerCase()}`}
                              className="block py-2 px-4 text-sm text-white/70 hover:text-white"
                            >
                              {sub}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="border-t border-white/10 mt-2 pt-2 space-y-1">
                    <SheetClose asChild>
                      <Link
                        to="/rastreio"
                        className="flex items-center gap-2 py-3 px-4 text-sm font-medium text-white hover:bg-white/10 rounded-lg transition-colors"
                        data-testid="mobile-nav-rastreio"
                      >
                        <Truck className="h-4 w-4 text-violet-400" />
                        Rastrear meu pedido
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link
                        to="/sobre"
                        className="flex items-center gap-2 py-3 px-4 text-sm font-medium text-white hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <ShieldCheck className="h-4 w-4 text-green-500" />
                        Quem Somos / Segurança
                      </Link>
                    </SheetClose>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>

            {/* Cashback Badge — dentro do mesmo container do hamburger pra ficar grudado à esquerda */}
            {isAuthenticated && cashbackBalance > 0 && (
              <Link
                to="/minha-conta"
                data-testid="cashback-mobile-header"
              >
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all duration-200 ${
                  useDarkHeader 
                    ? 'bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30' 
                    : 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                }`}>
                  <Wallet className={`h-3.5 w-3.5 flex-shrink-0 ${useDarkHeader ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-xs font-bold whitespace-nowrap ${useDarkHeader ? 'text-emerald-300' : 'text-emerald-700'}`}>
                    R$ {cashbackBalance.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </Link>
            )}
          </div>

          {/* Cashback Badge externo removido — agora junto ao hamburger acima */}

          {/* Logo */}
          <Link
            to="/"
            className={`flex-shrink-0 ${!isHomePage ? 'absolute left-1/2 -translate-x-1/2 md:relative md:left-auto md:translate-x-0 md:ml-[148px]' : ''}`}
            data-testid="logo-link"
          >
            <Logo isHomePage={isHomePage} />
          </Link>

          {/* Desktop Navigation - Clean minimal */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-12">
            {/* Todos os Produtos - primeiro */}
            <Link
              to="/produtos"
              className={`${textColorMuted} hover:${textColor} font-medium text-sm transition-colors py-4 whitespace-nowrap`}
              data-testid="nav-todos"
            >
              Todos os Produtos
            </Link>
            
            {['Masculino', 'Feminino', 'Infantil'].map((cat) => (
              <div key={cat} className="relative group">
                <Link
                  to={`/produtos/${cat.toLowerCase()}`}
                  className={`${textColorMuted} hover:${textColor} font-medium text-sm transition-colors py-4 flex items-center gap-1.5 whitespace-nowrap`}
                  data-testid={`nav-${cat.toLowerCase()}`}
                >
                  {cat}
                  <svg 
                    className={`w-3 h-3 ${useDarkHeader ? 'text-white/40 group-hover:text-white/70' : 'text-gray-400 group-hover:text-gray-700'} transition-transform duration-200 group-hover:translate-y-0.5`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Link>
                {/* Dropdown */}
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-neutral-100">
                    {subcategories[cat]?.map((sub) => (
                      <Link
                        key={sub}
                        to={`/produtos/${cat.toLowerCase()}?subcategory=${sub.toLowerCase()}`}
                        className="block px-4 py-2 text-sm text-neutral-600 hover:bg-gray-50 hover:text-[#0a0a0a]"
                      >
                        {sub}
                      </Link>
                    ))}
                    <div className="border-t border-neutral-100 mt-2 pt-2">
                      <Link
                        to={`/produtos/${cat.toLowerCase()}`}
                        className="block px-4 py-2 text-sm font-medium text-[#0a0a0a]"
                      >
                        Ver tudo em {cat}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center space-x-2 md:space-x-3">
            {/* Search - Desktop with autocomplete */}
            <div className="hidden md:block relative" ref={searchRef}>
              <form onSubmit={handleSearch}>
                <Input
                  type="search"
                  placeholder="Buscar vestido, camiseta..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                  className={`w-48 lg:w-64 pl-10 search-input rounded-full ${searchBg} ${textColorPlaceholder}`}
                  data-testid="search-input-desktop"
                />
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${searchIconColor}`} />
              </form>
              
              {/* Autocomplete dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-white shadow-lg rounded-lg border z-50 overflow-hidden">
                  {searchSuggestions.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => handleSuggestionClick(product)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white text-left"
                    >
                      <img 
                        src={product.images[0]} 
                        alt="" 
                        className="w-10 h-10 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                        <p className="text-xs text-[#0a0a0a] font-semibold">
                          R$ {product.price.toFixed(2).replace('.', ',')}
                        </p>
                      </div>
                    </button>
                  ))}
                  <Link
                    to={`/produtos?search=${encodeURIComponent(searchQuery)}`}
                    className="block p-3 text-center text-sm text-[#0a0a0a] font-medium bg-white hover:bg-neutral-100"
                    onClick={() => setShowSuggestions(false)}
                  >
                    Ver todos os resultados
                  </Link>
                </div>
              )}
            </div>

            {/* Search - Mobile */}
            <Button 
              variant="ghost" 
              size="icon"
              className={`md:hidden ${textColor} ${hoverBg}`}
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="search-btn-mobile"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* User Account - Desktop */}
            <div className="hidden md:block relative" ref={userMenuRef}>
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full ${hoverBg} transition-colors`}
                    data-testid="user-menu-btn"
                  >
                    <User className={`h-5 w-5 ${textColor}`} />
                    <span className={`text-sm font-medium ${textColor} max-w-[100px] truncate`}>
                      {user?.name?.split(' ')[0] || 'Conta'}
                    </span>
                    <ChevronDown className={`h-3 w-3 ${textColorMuted} transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 bg-white shadow-lg rounded-lg border border-gray-100 py-2 min-w-[200px] z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      {cashbackBalance > 0 && (
                        <div className="px-4 py-2.5 border-b border-gray-100 bg-emerald-50/60" data-testid="cashback-balance-desktop">
                          <div className="flex items-center gap-2">
                            <Wallet className="h-4 w-4 text-emerald-600" />
                            <div>
                              <p className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide">Cashback</p>
                              <p className="text-sm font-bold text-emerald-700">R$ {cashbackBalance.toFixed(2).replace('.', ',')}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      <Link
                        to="/minha-conta"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="user-menu-minha-conta"
                      >
                        <Wallet className="h-4 w-4" />
                        Minha Conta / Cashback
                      </Link>
                      <Link
                        to="/rastreio"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="user-menu-rastreio"
                      >
                        <Truck className="h-4 w-4 text-violet-600" />
                        Rastrear pedido
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="user-menu-admin"
                        >
                          <ShieldCheck className="h-4 w-4" />
                          Painel Admin
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        data-testid="user-menu-logout"
                      >
                        <LogOut className="h-4 w-4" />
                        Sair
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <Link to="/entrar" data-testid="header-login-link">
                  <Button variant="ghost" className={`${textColor} ${hoverBg} gap-2 text-sm`}>
                    <User className="h-5 w-5" />
                    Entrar
                  </Button>
                </Link>
              )}
            </div>

            {/* Cashback Badge - Desktop only (mobile aparece à esquerda da logo) */}
            {isAuthenticated && cashbackBalance > 0 && (
              <Link 
                to="/minha-conta" 
                className={`hidden md:flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-full transition-all duration-200 ${
                  useDarkHeader 
                    ? 'bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30' 
                    : 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                }`}
                data-testid="cashback-header-badge"
              >
                <Wallet className={`h-3.5 w-3.5 ${useDarkHeader ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className={`text-xs font-bold ${useDarkHeader ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  R$ {cashbackBalance.toFixed(2).replace('.', ',')}
                </span>
              </Link>
            )}

            {/* Rastrear (visible on mobile + desktop; text label only on lg+) */}
            <Link to="/rastreio" className={`flex items-center gap-1.5 px-2 md:px-2.5 py-1.5 rounded-full transition-all ${hoverBg}`} data-testid="header-track-link" title="Rastrear pedido" aria-label="Rastrear pedido">
              <Truck className={`h-5 w-5 md:h-4 md:w-4 ${textColor}`} />
              <span className={`text-xs font-medium ${textColor} hidden lg:inline`}>Rastrear</span>
            </Link>

            <Link to="/carrinho" className="relative" data-testid="cart-link" onClick={() => trackCartOpened()}>
              <Button variant="ghost" size="icon" className={`${textColor} ${hoverBg}`}>
                <ShoppingBag className="h-5 w-5" data-testid="header-cart-icon" />
                {cartCount > 0 && (
                  <span className="cart-badge bg-[#C41E3A]" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>

        {/* Mobile search bar with autocomplete */}
        {searchOpen && (
          <div className="md:hidden pb-4 animate-fade-in" ref={searchRef}>
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Input
                  type="search"
                  placeholder="Buscar vestido, camiseta..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 search-input rounded-full"
                  autoFocus
                  data-testid="search-input-mobile"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setSearchOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </form>
            
            {/* Mobile autocomplete */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="mt-2 bg-white shadow-lg rounded-lg border overflow-hidden">
                {searchSuggestions.map((product) => (
                  <button
                    key={product.product_id}
                    onClick={() => handleSuggestionClick(product)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left border-b last:border-b-0"
                  >
                    <img 
                      src={product.images[0]} 
                      alt="" 
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-sm text-[#C41E3A] font-semibold">
                        R$ {product.price.toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
